import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { LandingHeader } from '../components/layout/landing-header';

const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <LandingHeader />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h2>
        
        <div className="prose prose-lg text-gray-600">
          <p className="text-sm text-gray-500 mb-6">Last updated: March 8, 2026</p>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. Introduction</h3>
          <p>
            PageMind ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
            explains how we collect, use, disclose, and safeguard your information when you use our service.
          </p>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. Information We Collect</h3>
          <h4 className="text-xl font-medium text-gray-800 mt-6 mb-3">Personal Information</h4>
          <ul className="list-disc pl-6 space-y-2">
            <li>Name and email address when you create an account</li>
            <li>Company information if provided</li>
            <li>Payment information (processed securely through Stripe)</li>
          </ul>
          
          <h4 className="text-xl font-medium text-gray-800 mt-6 mb-3">Website Data</h4>
          <ul className="list-disc pl-6 space-y-2">
            <li>Content from your website pages for AI training</li>
            <li>Visitor interactions with the chatbot</li>
            <li>Analytics data about chatbot usage</li>
          </ul>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. How We Use Your Information</h3>
          <p>We use the collected information to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Provide and maintain our chatbot service</li>
            <li>Train AI models specific to your website content</li>
            <li>Process payments and manage subscriptions</li>
            <li>Send service updates and support communications</li>
            <li>Improve our service and develop new features</li>
          </ul>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. Data Storage and Security</h3>
          <p>
            We implement appropriate technical and organizational measures to protect your data against 
            unauthorized access, alteration, disclosure, or destruction. Your data is stored on secure 
            servers within the European Union.
          </p>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Google Integration Data</h3>
          <p>
            When you connect your Google account through our integrations feature, PageMind accesses data
            from Google services you authorize, including Google Search Console, Google Analytics 4,
            Google Ads, and Google Merchant Center. This data is accessed in <strong>read-only</strong> mode
            through Google's OAuth 2.0 protocol.
          </p>
          <h4 className="text-xl font-medium text-gray-800 mt-6 mb-3">What We Access</h4>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Search Console:</strong> Search queries, clicks, impressions, and average position for your website.</li>
            <li><strong>Analytics 4:</strong> Sessions, engagement metrics, and conversion data for your website.</li>
            <li><strong>Google Ads:</strong> Search terms, Quality Score, cost data, and landing page performance.</li>
            <li><strong>Merchant Center:</strong> Product listing status, disapprovals, and Shopping performance data.</li>
          </ul>
          <h4 className="text-xl font-medium text-gray-800 mt-6 mb-3">How We Use Google Data</h4>
          <p>
            Google data is used solely to display performance analytics within your PageMind dashboard and
            to inform AI-driven content optimization recommendations for your website.
          </p>
          <h4 className="text-xl font-medium text-gray-800 mt-6 mb-3">How We Store and Protect Google Data</h4>
          <p>
            Google OAuth tokens are encrypted at rest using AES-256-GCM. Performance data is stored in our
            database with access restricted to the authenticated site owner. We do not cache raw Google API
            responses beyond what is necessary for dashboard display.
          </p>
          <h4 className="text-xl font-medium text-gray-800 mt-6 mb-3">What We Do Not Do with Google Data</h4>
          <ul className="list-disc pl-6 space-y-2">
            <li>We do not sell, share, or transfer Google user data to third parties.</li>
            <li>We do not use Google user data for advertising or profiling.</li>
            <li>We do not use Google user data for purposes unrelated to the functionality described above.</li>
          </ul>
          <h4 className="text-xl font-medium text-gray-800 mt-6 mb-3">Revoking Google Access</h4>
          <p>
            You can disconnect your Google account at any time from Settings → Integrations in the PageMind
            dashboard. This immediately revokes our access tokens. You can also revoke access from your{' '}
            <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              Google Account permissions page
            </a>. Upon disconnection, synced performance data is retained in your account but no further data is fetched.
          </p>
          <p>
            PageMind's use and transfer of information received from Google APIs adheres to the{' '}
            <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              Google API Services User Data Policy
            </a>, including the Limited Use requirements.
          </p>

          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. Data Sharing</h3>
          <p>We do not sell, trade, or rent your personal information. We may share your information with:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Service providers who assist in operating our service (e.g., hosting, payment processing)</li>
            <li>Law enforcement when required by law</li>
            <li>Other parties with your explicit consent</li>
          </ul>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Your Rights (GDPR)</h3>
          <p>Under the General Data Protection Regulation (GDPR), you have the right to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Access your personal data</li>
            <li>Rectify inaccurate personal data</li>
            <li>Request erasure of your personal data</li>
            <li>Object to processing of your personal data</li>
            <li>Data portability</li>
            <li>Withdraw consent at any time</li>
          </ul>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">8. Cookies</h3>
          <p>
            We use essential cookies to maintain your session and preferences. Analytics cookies are used 
            only with your consent to understand how visitors interact with our service.
          </p>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">9. Children's Privacy</h3>
          <p>
            Our service is not intended for children under 16 years of age. We do not knowingly collect 
            personal information from children under 16.
          </p>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">10. Changes to This Policy</h3>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes by 
            posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>
          
          <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">11. Contact Us</h3>
          <p>
            If you have any questions about this Privacy Policy, please contact us at:
          </p>
          <ul className="list-none space-y-1 mt-4">
            <li>Email: privacy@pagemind.ai</li>
            <li>Address: [Company Address]</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;